import type { AmountFees, Discount } from '@hero/schema';

export interface ComputeSubtotalsInput {
  /** Items amount BEFORE any discount, fees, or tax (i.e. the load price). */
  itemsOriginalSubtotal: number;
  /** Discount block — only `amount` participates in the math. */
  discount: Pick<Discount, 'amount'>;
  /**
   * Pre-computed fee rollups (typically the output of {@link computeFeeTotals}).
   * `total`, `realized`, `subtracted`, `added` are all consumed.
   */
  fees: Pick<AmountFees, 'total' | 'realized' | 'subtracted' | 'added'>;
}

export interface SubtotalsResult {
  /** `itemsOriginalSubtotal − discount.amount` (post-discount, pre-fees). */
  itemsSubtotal: number;
  /** `itemsSubtotal + fees.total` (post-discount, post-fees, pre-tax). */
  subtotal: number;
  /**
   * `itemsSubtotal − fees.realized − fees.subtracted + fees.added`.
   *
   * Realized subtotal is what the party "actually receives" before tax —
   * realized fees are deducted because they're acknowledged costs that
   * don't change the customer-facing subtotal.
   */
  realizedSubtotal: number;
}

/**
 * Pure mirror of `AmountService.calcSubtotals`. Derives the three
 * subtotal flavours from `itemsOriginalSubtotal`, the discount block,
 * and the fee rollups.
 *
 * Order of operations matches the source method exactly.
 */
export function computeSubtotals(input: ComputeSubtotalsInput): SubtotalsResult {
  const itemsSubtotal = input.itemsOriginalSubtotal - input.discount.amount;
  const subtotal = itemsSubtotal + input.fees.total;
  const realizedSubtotal =
    itemsSubtotal - input.fees.realized - input.fees.subtracted + input.fees.added;

  return { itemsSubtotal, subtotal, realizedSubtotal };
}
